// src/pages/schemaValidationUtils.js

export const PII_TYPE_OPTIONS = [
  { value: 'card', label: 'Payment Card (PAN — Luhn FPE)', defaultFpe: true, supportsLuhn: true },
  { value: 'cvv', label: 'Card CVV / CVC Security Code', defaultFpe: false, supportsLuhn: false },
  { value: 'ssn', label: 'National ID / SSN', defaultFpe: true, supportsLuhn: false },
  { value: 'tax_id', label: 'Tax ID / PAN / EIN', defaultFpe: true, supportsLuhn: false },
  { value: 'email', label: 'Email Address', defaultFpe: false, supportsLuhn: false },
  { value: 'phone', label: 'Phone / Mobile Number', defaultFpe: false, supportsLuhn: false },
  { value: 'name', label: 'Full Name / Cardholder Name', defaultFpe: false, supportsLuhn: false },
  { value: 'date', label: 'Date / Card Expiration', defaultFpe: true, supportsLuhn: false },
  { value: 'dob', label: 'Date of Birth (DOB)', defaultFpe: true, supportsLuhn: false },
  { value: 'address', label: 'Street / Billing Address', defaultFpe: true, supportsLuhn: false },
  { value: 'zip', label: 'ZIP / Postal Code', defaultFpe: true, supportsLuhn: false },
  { value: 'ip', label: 'IP Address (IPv4 / IPv6)', defaultFpe: true, supportsLuhn: false },
  { value: 'custom_regex', label: 'Custom Regex Pattern', defaultFpe: false, supportsLuhn: false },
  { value: 'generic', label: 'Generic / Financial Field', defaultFpe: true, supportsLuhn: false }
];

export const ALLOWED_PII_TYPES = new Set([
  'card', 'credit_card', 'pan', 'cvv', 'ssn', 'tax_id',
  'email', 'phone', 'ip', 'name', 'date', 'dob',
  'address', 'zip', 'financial', 'custom_regex', 'generic'
]);

export const MASK_STRATEGY_OPTIONS = [
  { value: 'TOKENIZE', label: 'TOKENIZE (Reversible Enclave Token)' },
  { value: 'PARTIAL', label: 'PARTIAL (Partial Mask e.g. j***@example.com)' },
  { value: 'HASH', label: 'HASH (Deterministic SHA-256)' },
  { value: 'REDACT', label: 'REDACT (Permanent Redaction)' }
];

export const ALLOWED_MASK_STRATEGIES = new Set([
  'TOKENIZE', 'REDACT', 'PARTIAL', 'HASH', 'FPE'
]);

const FIELD_NAME_REGEX = /^[a-zA-Z0-9_.$[\]-]{1,200}$/;

export const SAMPLE_PAYMENT_PAYLOAD = JSON.stringify({
  id: "txn_19Q28374920198",
  status: "APPROVED",
  submitTimeUtc: "2026-10-18T18:00:00Z",
  clientReferenceInformation: {
    code: "ORDER_98765",
    comments: "E-commerce visa card transaction"
  },
  paymentInformation: {
    card: {
      network: "VISA",
      type: "CREDIT",
      accountNumber: "411111******1111",
      expirationDate: {
        month: "12",
        year: "2028"
      },
      cardholderName: "Jane Doe",
      cvv2: "***"
    }
  },
  orderInformation: {
    amountDetails: {
      totalAmount: "150.00",
      currency: "USD"
    }
  },
  processorInformation: {
    approvalCode: "884921",
    responseCode: "00",
    avsResponse: "Y",
    cvvResponse: "M"
  }
}, null, 2);

export const SAMPLE_CUSTOMER_HR_PAYLOAD = JSON.stringify({
  SSN: "123-45-6789",
  Name: "Vikram Malhotra",
  email: "vikram.malhotra@enterprise.com",
  phone: "+91 79055 45978",
  Salary: "185000.00",
  address: "Hindon Vihar, Sector 49, Noida, UP 201301"
}, null, 2);

/**
 * Converts Visual Field Builder array into formatted Vaultize Schema JSON string.
 */
export const convertFieldsToJson = (fieldsList) => {
  const fieldsObj = {};
  fieldsList.forEach((f) => {
    if (!f.name || !f.name.trim()) return;
    const def = {
      piiType: f.piiType || 'generic',
      required: Boolean(f.required)
    };
    if (f.maskStrategy) {
      def.maskStrategy = f.maskStrategy;
    }
    if (f.formatPreserving) {
      def.formatPreserving = true;
    }
    if (f.preserveLuhn && (f.piiType === 'card' || f.piiType === 'credit_card' || f.piiType === 'pan')) {
      def.preserveLuhn = true;
    }
    if (f.piiType === 'custom_regex' && f.regex && f.regex.trim()) {
      def.regex = f.regex.trim();
    }
    if (f.description && f.description.trim()) {
      def.description = f.description.trim();
    }
    fieldsObj[f.name.trim()] = def;
  });
  return JSON.stringify({ type: 'object', fields: fieldsObj }, null, 2);
};

/**
 * Parses a Vaultize Schema JSON string into Visual Field Builder array.
 * Returns null if the JSON is not a valid Vaultize Schema structure.
 */
export const parseJsonToFields = (jsonString) => {
  try {
    const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || !parsed.fields || typeof parsed.fields !== 'object' || Array.isArray(parsed.fields)) {
      return null;
    }
    const entries = Object.entries(parsed.fields);
    if (entries.length === 0) return null;

    for (const [, f] of entries) {
      if (!f || typeof f !== 'object' || Array.isArray(f) || typeof f.piiType !== 'string') {
        return null;
      }
    }

    return entries.map(([fieldName, f]) => ({
      name: fieldName,
      piiType: (f.piiType || 'generic').toLowerCase(),
      required: f.required !== undefined ? Boolean(f.required) : true,
      formatPreserving: Boolean(f.formatPreserving),
      preserveLuhn: Boolean(f.preserveLuhn),
      maskStrategy: f.maskStrategy || 'TOKENIZE',
      regex: f.regex || '',
      description: f.description || ''
    }));
  } catch {
    return null;
  }
};

/**
 * Strictly validates that a JSON string or object is a valid Vaultize Schema
 * and NOT an arbitrary raw JSON data payload.
 */
export const validateVaultizeSchema = (jsonInput) => {
  const errors = [];
  const warnings = [];
  let isRawPayload = false;
  let parsed = null;

  try {
    parsed = typeof jsonInput === 'string' ? JSON.parse(jsonInput) : jsonInput;
  } catch (e) {
    return {
      valid: false,
      isRawPayload: false,
      fieldCount: 0,
      errors: [`Invalid JSON syntax: ${e.message}`],
      warnings: [],
      parsed: null
    };
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {
      valid: false,
      isRawPayload: false,
      fieldCount: 0,
      errors: ['Schema definition must be a JSON object: {"type": "object", "fields": { ... }}.'],
      warnings: [],
      parsed: null
    };
  }

  if (!parsed.fields || typeof parsed.fields !== 'object' || Array.isArray(parsed.fields)) {
    return {
      valid: false,
      isRawPayload: true,
      fieldCount: 0,
      errors: [
        'Invalid Schema Format: The JSON provided looks like a raw data payload rather than a Vaultize Schema. A valid schema must have {"type": "object", "fields": { "<fieldName>": { "piiType": "..." } }}. Click "Convert Payload to Schema (AI)" below to automatically convert this JSON payload into a valid Vaultize Schema.'
      ],
      warnings: [],
      parsed
    };
  }

  if (parsed.type !== 'object') {
    errors.push('Root schema must specify "type": "object".');
  }

  const fieldEntries = Object.entries(parsed.fields);
  if (fieldEntries.length === 0) {
    errors.push('Schema "fields" object cannot be empty. Define at least one field.');
  }

  fieldEntries.forEach(([fieldName, fieldDef]) => {
    if (!fieldName || !fieldName.trim()) {
      errors.push('Field name cannot be empty.');
      return;
    }
    if (!FIELD_NAME_REGEX.test(fieldName)) {
      errors.push(`Field "${fieldName}" contains unsupported characters. Use letters, numbers, underscores, hyphens, or dot-paths.`);
    }

    if (!fieldDef || typeof fieldDef !== 'object' || Array.isArray(fieldDef)) {
      isRawPayload = true;
      errors.push(`Field "${fieldName}" must be a schema rule object (with "piiType"), not a raw value.`);
      return;
    }

    if (!fieldDef.piiType || typeof fieldDef.piiType !== 'string' || !fieldDef.piiType.trim()) {
      isRawPayload = true;
      errors.push(`Field "${fieldName}" is missing required "piiType" property (e.g., card, ssn, email, phone, name, generic).`);
      return;
    }

    const piiType = fieldDef.piiType.trim().toLowerCase();
    if (!ALLOWED_PII_TYPES.has(piiType)) {
      errors.push(
        `Field "${fieldName}" has unsupported piiType "${fieldDef.piiType}". Supported: card, cvv, ssn, tax_id, email, phone, ip, name, date, dob, address, zip, custom_regex, generic.`
      );
    }

    if (fieldDef.maskStrategy !== undefined && fieldDef.maskStrategy !== null) {
      if (typeof fieldDef.maskStrategy !== 'string' || !ALLOWED_MASK_STRATEGIES.has(fieldDef.maskStrategy.trim().toUpperCase())) {
        errors.push(
          `Field "${fieldName}" has invalid maskStrategy "${fieldDef.maskStrategy}". Supported: TOKENIZE, PARTIAL, HASH, REDACT.`
        );
      }
    }

    if (fieldDef.required !== undefined && typeof fieldDef.required !== 'boolean') {
      errors.push(`Field "${fieldName}" property "required" must be a boolean (true or false).`);
    }

    if (fieldDef.formatPreserving !== undefined && typeof fieldDef.formatPreserving !== 'boolean') {
      errors.push(`Field "${fieldName}" property "formatPreserving" must be a boolean (true or false).`);
    }

    if (fieldDef.preserveLuhn !== undefined && typeof fieldDef.preserveLuhn !== 'boolean') {
      errors.push(`Field "${fieldName}" property "preserveLuhn" must be a boolean (true or false).`);
    }

    if (piiType === 'custom_regex' && fieldDef.regex) {
      try {
        new RegExp(fieldDef.regex);
      } catch (regexErr) {
        errors.push(`Field "${fieldName}" has invalid regex pattern: ${regexErr.message}`);
      }
    }
  });

  return {
    valid: errors.length === 0,
    isRawPayload,
    fieldCount: fieldEntries.length,
    errors,
    warnings,
    parsed
  };
};

/**
 * Local fallback converter if offline or if user converts directly in client.
 */
export const inferSchemaClientSide = (sampleObj, filterMode = 'sensitive_only', pathStyle = 'leaf_name') => {
  const root = Array.isArray(sampleObj) && sampleObj.length > 0 ? sampleObj[0] : sampleObj;
  const leafList = [];

  const traverse = (node, prefix) => {
    if (!node || typeof node !== 'object') return;
    Object.entries(node).forEach(([k, v]) => {
      const dotPath = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        traverse(v, dotPath);
      } else if (Array.isArray(v)) {
        if (v.length > 0 && typeof v[0] === 'object') {
          traverse(v[0], dotPath);
        } else {
          leafList.push(classifyFieldLocal(k, dotPath, v.length > 0 ? String(v[0]) : ''));
        }
      } else {
        leafList.push(classifyFieldLocal(k, dotPath, v !== null && v !== undefined ? String(v) : ''));
      }
    });
  };

  traverse(root, '');

  const sensitiveOnly = filterMode !== 'all_fields';
  const useDotPath = pathStyle === 'dot_path';
  const fields = {};
  const detectedFields = [];
  const skippedNonPiiFields = [];

  const counts = {};
  leafList.forEach((f) => {
    counts[f.leafName] = (counts[f.leafName] || 0) + 1;
  });

  leafList.forEach((f) => {
    let fieldKey = useDotPath ? f.dotPath : f.leafName;
    if (!useDotPath && counts[f.leafName] > 1 && f.dotPath.includes('.')) {
      const parts = f.dotPath.split('.');
      fieldKey = `${parts[parts.length - 2]}_${f.leafName}`;
    }

    const meta = { ...f, fieldKey, jsonPath: f.dotPath };
    if (sensitiveOnly && !f.sensitive) {
      skippedNonPiiFields.push(meta);
      return;
    }

    detectedFields.push(meta);
    const rule = {
      piiType: f.piiType,
      required: f.required,
      maskStrategy: f.maskStrategy
    };
    if (f.formatPreserving) rule.formatPreserving = true;
    if (f.preserveLuhn) rule.preserveLuhn = true;
    if (f.description) rule.description = f.description;
    fields[fieldKey] = rule;
  });

  if (Object.keys(fields).length === 0) {
    leafList.forEach((f) => {
      const fieldKey = useDotPath ? f.dotPath : f.leafName;
      fields[fieldKey] = {
        piiType: f.piiType,
        required: f.required,
        maskStrategy: f.maskStrategy,
        ...(f.formatPreserving ? { formatPreserving: true } : {}),
        ...(f.description ? { description: f.description } : {})
      };
    });
  }

  return {
    schemaJson: { type: 'object', fields },
    suggestedName: root.paymentInformation || root.card ? 'payment_card_transaction_v1' : 'customer_pii_schema_v1',
    suggestedDescription: `Auto-generated Vaultize schema (${Object.keys(fields).length} protected fields inferred from sample JSON payload)`,
    engineUsed: 'VAULTIZE_SEMANTIC_AI',
    detectedFields,
    skippedNonPiiFields
  };
};

const classifyFieldLocal = (leafName, dotPath, sampleValue) => {
  const lowerKey = leafName.toLowerCase();
  const lowerPath = dotPath.toLowerCase();
  const val = (sampleValue || '').trim();

  if (
    lowerKey.includes('accountnumber') ||
    lowerKey.includes('cardnumber') ||
    lowerKey === 'pan' ||
    (lowerPath.includes('card') && lowerKey.includes('number'))
  ) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'card',
      maskStrategy: 'TOKENIZE',
      required: true,
      formatPreserving: true,
      preserveLuhn: true,
      sensitive: true,
      description: 'Payment card primary account number (PAN)'
    };
  }

  if (lowerKey === 'cvv' || lowerKey === 'cvv2' || lowerKey === 'cvc' || lowerKey.includes('securitycode')) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'cvv',
      maskStrategy: 'REDACT',
      required: true,
      formatPreserving: false,
      preserveLuhn: false,
      sensitive: true,
      description: 'Card verification security code (CVV/CVC)'
    };
  }

  if (lowerKey.includes('ssn') || lowerKey.includes('socialsecurity') || lowerKey.includes('nationalid')) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'ssn',
      maskStrategy: 'HASH',
      required: true,
      formatPreserving: true,
      preserveLuhn: false,
      sensitive: true,
      description: 'Social Security or national ID number'
    };
  }

  if (lowerKey.includes('email') || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'email',
      maskStrategy: 'PARTIAL',
      required: true,
      formatPreserving: false,
      preserveLuhn: false,
      sensitive: true,
      description: 'Primary customer email'
    };
  }

  if (lowerKey.includes('phone') || lowerKey.includes('mobile') || lowerKey.includes('whatsapp')) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'phone',
      maskStrategy: 'PARTIAL',
      required: true,
      formatPreserving: false,
      preserveLuhn: false,
      sensitive: true,
      description: 'Contact phone number'
    };
  }

  if (
    lowerKey.includes('cardholdername') ||
    lowerKey.includes('fullname') ||
    lowerKey.includes('firstname') ||
    lowerKey.includes('lastname') ||
    lowerKey.includes('customername') ||
    lowerKey === 'name'
  ) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'name',
      maskStrategy: 'PARTIAL',
      required: true,
      formatPreserving: false,
      preserveLuhn: false,
      sensitive: true,
      description: lowerPath.includes('card') ? 'Cardholder full name' : 'Customer full name'
    };
  }

  if (lowerPath.includes('expiration') || lowerPath.includes('expiry')) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'date',
      maskStrategy: 'REDACT',
      required: true,
      formatPreserving: true,
      preserveLuhn: false,
      sensitive: true,
      description: `Card expiration ${leafName}`
    };
  }

  if (lowerKey.includes('address') || lowerKey.includes('street') || lowerKey.includes('city')) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'address',
      maskStrategy: 'REDACT',
      required: true,
      formatPreserving: true,
      preserveLuhn: false,
      sensitive: true,
      description: 'Physical or mailing address'
    };
  }

  if (lowerKey.includes('salary') || lowerKey.includes('income') || lowerKey.includes('amount') || lowerKey.includes('balance')) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'generic',
      maskStrategy: 'REDACT',
      required: true,
      formatPreserving: true,
      preserveLuhn: false,
      sensitive: true,
      description: `Financial value (${leafName})`
    };
  }

  if (lowerKey.includes('approvalcode') || lowerKey === 'code' || lowerKey.includes('reference')) {
    return {
      leafName,
      dotPath,
      sampleValue: val,
      piiType: 'generic',
      maskStrategy: 'HASH',
      required: false,
      formatPreserving: true,
      preserveLuhn: false,
      sensitive: true,
      description: `Reference / approval identifier (${leafName})`
    };
  }

  return {
    leafName,
    dotPath,
    sampleValue: val,
    piiType: 'generic',
    maskStrategy: 'TOKENIZE',
    required: false,
    formatPreserving: false,
    preserveLuhn: false,
    sensitive: false,
    description: `Operational field (${dotPath})`
  };
};
